import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PortalUiProvider, usePortalUi } from './contexts/PortalUiContext';
import SignInPage from './pages/SignInPage';
import TripsPage from './pages/TripsPage';
import ChartsPage from './pages/ChartsPage';
import MapsPage from './pages/MapsPage';
import BucketPage from './pages/BucketPage';
import PortalPlanPage from './pages/PortalPlanPage';
import PortalOAuthPage from './pages/PortalOAuthPage';
import { TimeFilterProvider } from './contexts/TimeFilterContext';
import { SchedulerStatusProvider } from './contexts/SchedulerStatusContext';
import { PublishAccessProvider } from './contexts/PublishAccessContext';
import SideNav from './components/SideNav';
import Header from './components/Header';
import PortalOAuthSkeleton from './components/portal-oauth/PortalOAuthSkeleton';
import { PortalEmbedBridge } from './portal-oauth/embed';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queries/queryClient';
import { useUserConfig, getCompanyNameFromUserConfig } from './queries/useUserConfig';

function AppContent() {
  const { user, loading } = useAuth();
  const { isShowSidenav, isDisablePlatform } = usePortalUi();
  const { data: userConfigData } = useUserConfig({ enabled: Boolean(user) });
  const companyName = getCompanyNameFromUserConfig(userConfigData?.config);

  const isPortalReload = loading && (() => {
    try { return sessionStorage.getItem('portal_oauth_reloading') === '1'; } catch { return false; }
  })();

  useEffect(() => {
    if (!loading) {
      try { sessionStorage.removeItem('portal_oauth_reloading'); } catch {}
    }
  }, [loading]);

  if (loading) {
    if (isPortalReload) return <PortalOAuthSkeleton isDisablePlatform={isDisablePlatform} />;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <SignInPage />;
  }

  return (
    <SchedulerStatusProvider>
      <TimeFilterProvider>
        <PublishAccessProvider>
          {/* iframe parent nav — only when portal embed hides sidenav */}
          {!isShowSidenav && <PortalEmbedBridge />}
          <div className="flex h-screen overflow-hidden">
            {isShowSidenav && <SideNav />}
            <div className="flex flex-1 flex-col overflow-hidden">
              <Header companyName={companyName} />
              <main className="flex-1 overflow-auto bg-surface font-inter">
                <div className="w-full px-4 py-2">
                  <Routes>
                    <Route path="/" element={<TripsPage />} />
                    <Route path="/charts" element={<ChartsPage />} />
                    <Route path="/maps" element={<MapsPage />} />
                    <Route path="/bucket" element={<BucketPage />} />
                    <Route path="/portal-plan" element={<PortalPlanPage />} />
                  </Routes>
                </div>
              </main>
            </div>
          </div>
        </PublishAccessProvider>
      </TimeFilterProvider>
    </SchedulerStatusProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PortalUiProvider>
          <Router>
            <Routes>
              <Route path="/portal_oauth" element={<PortalOAuthPage />} />
              <Route path="*" element={<AppContent />} />
            </Routes>
          </Router>
        </PortalUiProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
